import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Check, Coins, Loader2, Building2, Gift } from "lucide-react";

const PACK_META = {
  p100: { tagline: "For quick experiments", highlight: false },
  p200: { tagline: "For side projects", highlight: false },
  p500: { tagline: "For serious builders", highlight: true },
  p1000: { tagline: "For power users", highlight: false },
  corporate: { tagline: "For teams & companies", highlight: false },
};

export default function Billing() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [packs, setPacks] = useState([]);
  const [buying, setBuying] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    api.get("/payments/packs").then((r) => setPacks(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const status = searchParams.get("payment");
    if (!status) return;
    if (status === "return") {
      const orderId = searchParams.get("order_id");
      if (orderId) {
        api.get(`/payments/cashfree/status/${orderId}`)
          .then((r) => {
            if (r.data.paid) {
              toast.success(`Payment successful! ${r.data.credits} credits added.`);
              refreshUser();
            } else {
              toast.error(`Payment ${String(r.data.status || "not completed").toLowerCase()}. No credits charged.`);
            }
          })
          .catch(() => toast.error("Could not verify payment. If money was deducted, it'll reflect shortly."));
      }
    } else if (status === "success") {
      toast.success("Payment successful! Credits added to your wallet.");
      refreshUser();
    } else if (status === "failed") {
      toast.error("Payment failed or cancelled. No credits were charged.");
    }
    searchParams.delete("payment");
    searchParams.delete("order_id");
    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCashfree = (mode) =>
    new Promise((resolve, reject) => {
      if (window.Cashfree) return resolve(window.Cashfree({ mode }));
      const s = document.createElement("script");
      s.src = "https://sdk.cashfree.com/js/v3/cashfree.js";
      s.onload = () => resolve(window.Cashfree({ mode }));
      s.onerror = () => reject(new Error("Failed to load Cashfree SDK"));
      document.body.appendChild(s);
    });

  const buy = async (pack) => {
    setBuying(pack.id);
    try {
      const { data } = await api.post("/payments/create", { pack_id: pack.id });
      if (data.provider === "cashfree") {
        const cashfree = await loadCashfree(data.mode === "production" ? "production" : "sandbox");
        await cashfree.checkout({ paymentSessionId: data.payment_session_id, redirectTarget: "_self" });
        return;
      }
      // PayU hosted checkout (auto-submit hidden form)
      const form = document.createElement("form");
      form.method = "POST";
      form.action = data.action;
      Object.entries(data.fields).forEach(([name, value]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = name;
        input.value = String(value);
        form.appendChild(input);
      });
      document.body.appendChild(form);
      form.submit();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not start payment.");
      setBuying(null);
    }
  };

  const standard = packs.filter((p) => p.id !== "corporate");
  const corporate = packs.find((p) => p.id === "corporate");

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white relative overflow-hidden">
      <div className="pointer-events-none absolute -top-20 left-1/2 -translate-x-1/2 w-[800px] h-[400px] blur-[140px] opacity-20" style={{ background: "radial-gradient(circle, #00FF66 0%, transparent 70%)" }} />
      <header className="border-b border-white/10 nig-glass relative z-20">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <button
            data-testid="billing-back"
            onClick={() => navigate("/app")}
            className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to app
          </button>
          <div
            data-testid="billing-credit-balance"
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#141415] border border-white/10"
          >
            <Coins className="w-4 h-4 text-[#00FF66]" />
            <span className="text-sm text-white font-medium">
              {user?.unlimited_credits ? "Unlimited" : `${(user?.credits ?? 0).toLocaleString()} credits`}
            </span>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-16 relative z-10">
        <div className="text-center max-w-xl mx-auto">
          <h1 className="font-heading font-extrabold text-4xl tracking-tighter">Buy credits</h1>
          <p className="text-zinc-400 mt-3">
            Top up your wallet and keep building. Credits never expire.
          </p>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 bg-[#141415] text-sm text-zinc-300 mt-6">
            <Gift className="w-4 h-4 text-[#00FF66]" />
            Gemini Pro = 1 · GPT-5.4 = 2 · Claude Sonnet = 3 credits / message
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
          {standard.map((p) => {
            const meta = PACK_META[p.id] || {};
            return (
              <div
                key={p.id}
                data-testid={`billing-card-${p.id}`}
                className={`relative flex flex-col p-6 rounded-2xl border transition-colors ${
                  meta.highlight ? "border-[#00FF66] bg-[#101a12]" : "border-white/10 bg-[#141415] hover:border-white/20"
                }`}
              >
                {meta.highlight && (
                  <span className="absolute -top-3 left-6 px-3 py-1 rounded-full bg-[#00FF66] text-black text-xs font-semibold">
                    Most popular
                  </span>
                )}
                <p className="text-sm text-zinc-500">{meta.tagline}</p>
                <div className="mt-4 flex items-end gap-1">
                  <span className="font-heading font-extrabold text-4xl tracking-tighter text-white">₹{p.amount}</span>
                </div>
                <p className="text-[#00FF66] font-mono text-sm mt-2">{p.credits.toLocaleString()} credits</p>
                <div className="h-px bg-white/10 my-5" />
                <ul className="space-y-2.5 text-sm text-zinc-400 flex-1">
                  <li className="flex gap-2"><Check className="w-4 h-4 text-[#00FF66] shrink-0 mt-0.5" /> All 3 AI models</li>
                  <li className="flex gap-2"><Check className="w-4 h-4 text-[#00FF66] shrink-0 mt-0.5" /> Never expires</li>
                </ul>
                <Button
                  data-testid={`billing-buy-${p.id}`}
                  onClick={() => buy(p)}
                  disabled={buying === p.id}
                  className={`mt-6 h-11 rounded-lg font-semibold transition-colors ${
                    meta.highlight ? "bg-[#00FF66] hover:bg-[#00CC52] text-black" : "bg-[#1F1F22] hover:bg-[#2a2a2e] text-white"
                  }`}
                >
                  {buying === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : `Buy for ₹${p.amount}`}
                </Button>
              </div>
            );
          })}
        </div>

        {corporate && (
          <div
            data-testid="billing-card-corporate"
            className="mt-6 rounded-2xl border border-white/10 bg-gradient-to-r from-[#141415] to-[#101a12] p-8 flex flex-col md:flex-row items-center justify-between gap-6"
          >
            <div className="flex items-center gap-5">
              <div className="w-14 h-14 rounded-2xl bg-[#00FF66]/10 flex items-center justify-center shrink-0">
                <Building2 className="w-7 h-7 text-[#00FF66]" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-2xl text-white">Corporate</h3>
                <p className="text-zinc-400 mt-1">
                  {corporate.credits.toLocaleString()} credits — ₹{corporate.amount.toLocaleString()} only
                </p>
              </div>
            </div>
            <Button
              data-testid="billing-buy-corporate"
              onClick={() => buy(corporate)}
              disabled={buying === "corporate"}
              className="h-12 px-8 bg-[#00FF66] hover:bg-[#00CC52] text-black font-semibold rounded-xl transition-colors"
            >
              {buying === "corporate" ? <Loader2 className="w-4 h-4 animate-spin" /> : `Buy for ₹${corporate.amount.toLocaleString()}`}
            </Button>
          </div>
        )}

        <p className="text-center text-xs text-zinc-600 mt-10">
          Payments are securely processed by Cashfree. Prices in INR (₹).
        </p>
        <div className="text-center text-xs text-zinc-600 mt-2 flex flex-wrap gap-x-3 gap-y-1 justify-center" data-testid="billing-policy-links">
          <button onClick={() => navigate("/terms")} className="hover:text-white transition-colors">Terms</button>
          <span>·</span>
          <button onClick={() => navigate("/privacy")} className="hover:text-white transition-colors">Privacy</button>
          <span>·</span>
          <button onClick={() => navigate("/refund")} className="hover:text-white transition-colors">Refund Policy</button>
          <span>·</span>
          <button onClick={() => navigate("/shipping")} className="hover:text-white transition-colors">Delivery</button>
          <span>·</span>
          <button onClick={() => navigate("/contact")} className="hover:text-white transition-colors">Contact</button>
        </div>
      </div>
    </div>
  );
}
